import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, formatDateTimeBR } from '../api'
import type { Clinic, ClinicDevice, User } from '../api'

// Painel do dono da nuvem: cadastra as clínicas e o administrador de cada
// uma, suspende acesso e bloqueia computadores. Não mostra dado clínico.

const EMPTY_FORM = { name: '', adminName: '', adminLogin: '', adminPassword: '' }

const COLS = '1.5fr 110px 120px 170px 230px'

export function Owner({ user, theme, onToggleTheme, onLogout }: {
  user: User
  theme: 'light' | 'dark'
  onToggleTheme: () => void
  onLogout: () => void
}) {
  const [clinics, setClinics] = useState<Clinic[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [busy, setBusy] = useState(false)
  const [openId, setOpenId] = useState('')

  const load = useCallback(async () => {
    try {
      setClinics((await api.owner.clinics()).items)
      setLoaded(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar as clínicas')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const created = await api.owner.createClinic(form)
      setNotice(`${created.name} cadastrada.`)
      setForm(EMPTY_FORM)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao cadastrar a clínica')
    } finally {
      setBusy(false)
    }
  }

  async function toggleActive(c: Clinic) {
    if (c.active && !confirm(`Suspender ${c.name}?`)) return
    setError('')
    setNotice('')
    try {
      await api.owner.updateClinic(c.id, { active: !c.active })
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao atualizar a clínica')
    }
  }

  const set = (key: keyof typeof EMPTY_FORM) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value })

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--text)' }}>
      <header style={{ flex: 'none', height: 46, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--primary)', color: 'var(--primary-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 12px/1 var(--font-mono)' }}>C</div>
        <div style={{ font: '600 14px/1 var(--font-sans)', letterSpacing: '-.01em' }}>
          CScan <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>Painel do fornecedor</span>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ font: '400 11.5px/1 var(--font-sans)', color: 'var(--text-2)' }}>{user.name}</span>
        <button className="btn" onClick={onToggleTheme} style={{ height: 26, padding: '0 9px', font: '500 10.5px/1 var(--font-mono)' }}>
          {theme === 'dark' ? 'TEMA ESCURO' : 'TEMA CLARO'}
        </button>
        <button className="btn" onClick={onLogout} style={{ height: 26 }}>Sair</button>
      </header>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 20px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div style={{ padding: '9px 12px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>}
          {notice && <div style={{ padding: '9px 12px', border: '1px solid var(--ok)', borderRadius: 6, background: 'var(--ok-soft)', fontSize: 12.5 }}>{notice}</div>}

          <form onSubmit={create} className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ font: '600 14px/1.2 var(--font-sans)' }}>Nova clínica</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, alignItems: 'end' }}>
              <label className="field"><span>Clínica</span>
                <input className="input" value={form.name} onChange={set('name')} required minLength={2} />
              </label>
              <label className="field"><span>Administrador</span>
                <input className="input" value={form.adminName} onChange={set('adminName')} required />
              </label>
              <label className="field"><span>Login</span>
                <input className="input" value={form.adminLogin} onChange={set('adminLogin')} required autoCapitalize="none" style={{ fontFamily: 'var(--font-mono)' }} />
              </label>
              <label className="field"><span>Senha</span>
                <input className="input" type="password" value={form.adminPassword} onChange={set('adminPassword')} required minLength={8} autoComplete="new-password" />
              </label>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? 'Cadastrando…' : 'Cadastrar'}
              </button>
            </div>
          </form>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ font: '600 14px/1.2 var(--font-sans)' }}>Clínicas</div>
              <div style={{ font: '400 11.5px/1 var(--font-mono)', color: 'var(--text-3)' }}>{clinics.length}</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 820 }}>
                <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '7px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                  <div>Clínica</div><div>Situação</div><div>Computadores</div><div>Última sincronização</div><div>Ações</div>
                </div>
                {loaded && clinics.length === 0 && (
                  <div style={{ padding: '22px 16px', font: '400 12.5px/1.5 var(--font-sans)', color: 'var(--text-3)' }}>
                    Nenhuma clínica
                  </div>
                )}
                {clinics.map((c) => (
                  <div key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '9px 16px', alignItems: 'center', font: '400 12.5px/1.3 var(--font-sans)' }}>
                      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      <div>
                        <span className="badge" style={{ background: c.active ? 'var(--ok-soft)' : 'var(--danger-soft)', color: c.active ? 'var(--ok)' : 'var(--danger)' }}>
                          {c.active ? 'ATIVA' : 'SUSPENSA'}
                        </span>
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)' }}>{c.devices}</div>
                      <div style={{ color: 'var(--text-2)' }}>{c.lastSeenAt ? formatDateTimeBR(c.lastSeenAt) : '—'}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn" onClick={() => setOpenId(openId === c.id ? '' : c.id)} style={{ height: 26, fontSize: 11.5 }}>
                          {openId === c.id ? 'Fechar' : 'Computadores'}
                        </button>
                        <button className="btn" onClick={() => void toggleActive(c)} style={{ height: 26, fontSize: 11.5 }}>
                          {c.active ? 'Suspender' : 'Reativar'}
                        </button>
                      </div>
                    </div>
                    {openId === c.id && <Devices clinic={c} onChanged={load} />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const DEVICE_COLS = '1.3fr 100px 100px 1fr 160px 110px 100px'

// Computadores autorizados de uma clínica (cada um se cadastra no primeiro
// login online do aplicativo).
function Devices({ clinic, onChanged }: { clinic: Clinic; onChanged: () => Promise<void> }) {
  const [items, setItems] = useState<ClinicDevice[] | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setItems((await api.owner.devices(clinic.id)).items)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar os computadores')
    }
  }, [clinic.id])

  useEffect(() => { void load() }, [load])

  async function toggle(d: ClinicDevice) {
    const blocking = !d.revokedAt
    if (blocking && !confirm(`Bloquear ${d.name || d.id}?`)) return
    setError('')
    try {
      await api.owner.setDeviceRevoked(d.id, blocking)
      await Promise.all([load(), onChanged()])
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao atualizar o computador')
    }
  }

  return (
    <div style={{ padding: '4px 16px 14px', background: 'var(--surface-2)' }}>
      {error && <div style={{ margin: '8px 0', padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
      {items && items.length === 0 && (
        <div style={{ padding: '12px 0 2px', font: '400 12px/1.5 var(--font-sans)', color: 'var(--text-3)' }}>
          Nenhum computador
        </div>
      )}
      {items && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 780 }}>
            <div style={{ display: 'grid', gridTemplateColumns: DEVICE_COLS, padding: '10px 0 6px', font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
              <div>Computador</div><div>Sistema</div><div>Versão</div><div>Cadastrado por</div><div>Último contato</div><div>Situação</div><div />
            </div>
            {items.map((d) => (
              <div key={d.id} style={{ display: 'grid', gridTemplateColumns: DEVICE_COLS, padding: '6px 0', alignItems: 'center', borderTop: '1px solid var(--border)', font: '400 12px/1.3 var(--font-sans)' }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.id}>{d.name || d.id}</div>
                <div style={{ color: 'var(--text-2)' }}>{d.platform || '—'}</div>
                <div style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{d.appVersion || '—'}</div>
                <div style={{ fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.enrolledBy || '—'}</div>
                <div style={{ color: 'var(--text-2)' }}>{d.lastSeenAt ? formatDateTimeBR(d.lastSeenAt) : '—'}</div>
                <div>
                  <span className="badge" style={{ background: d.revokedAt ? 'var(--danger-soft)' : 'var(--ok-soft)', color: d.revokedAt ? 'var(--danger)' : 'var(--ok)' }}>
                    {d.revokedAt ? 'BLOQUEADO' : 'LIBERADO'}
                  </span>
                </div>
                <div>
                  <button className="btn" onClick={() => void toggle(d)} style={{ height: 24, fontSize: 11 }}>
                    {d.revokedAt ? 'Liberar' : 'Bloquear'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
